// src/pages/affiliate/CampaignsPage.tsx
// A partner's own campaigns — one per offer they're promoting. Detail is a
// right rail (tracking link, referral links, quick stats), not a page.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useAffiliateCampaign, useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { authStore } from '@core/stores';
import type { Campaign } from '@core/types/affiliate';
import { CampaignDetailRail } from '@/features/affiliate/CampaignDetailRail';
import { CampaignFormDialog } from '@/features/affiliate/CampaignFormDialog';
import { CampaignStatusBadge } from '@/features/affiliate/badges';
import { errMessage, formatMoney, getStoredAffiliateId, listParams } from '@/features/affiliate/format';

export const CampaignsPage = observer(() => {
  const { t, locale } = useI18n();
  const store = useAffiliateCampaign();
  const profileId = authStore.activeProfileId;
  const myAffiliateId = getStoredAffiliateId(profileId);

  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Campaign | null>(null);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = () => {
    setError(null);
    store
      .ListCampaigns(listParams({ limit: 100 }, myAffiliateId ? { affiliate_id: myAffiliateId } : {}))
      .catch((e: unknown) => setError(errMessage(e, t('affiliate.campaigns.loadError'))));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myAffiliateId]);

  if (!myAffiliateId) {
    return (
      <div className="p-6">
        <Alert variant="info">{t('affiliate.dashboard.onboardHint')}</Alert>
        <Link to="/affiliate" className="mt-3 inline-block text-sm text-primary hover:underline">
          {t('affiliate.backToDashboard')}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <div className="min-w-0 flex-1 space-y-4 overflow-y-auto p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold text-foreground">{t('affiliate.campaigns.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('affiliate.campaigns.subtitle')}</p>
          </div>
          <Link to="/affiliate/offers" className="text-sm text-primary hover:underline">
            {t('affiliate.dashboard.browseOffers')}
          </Link>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        {store.isLoading && store.items.length === 0 && (
          <div className="space-y-2" aria-busy="true">
            <div className="h-16 animate-pulse rounded-md bg-muted" />
            <div className="h-16 animate-pulse rounded-md bg-muted" />
          </div>
        )}

        {!store.isLoading && store.items.length === 0 && !error && (
          <div className="rounded-md border border-dashed border-border p-8 text-center">
            <p className="text-sm text-muted-foreground">{t('affiliate.campaigns.empty')}</p>
            <Link to="/affiliate/offers" className="mt-3 inline-block text-sm text-primary hover:underline">
              {t('affiliate.dashboard.browseOffers')}
            </Link>
          </div>
        )}

        <div className="space-y-2">
          {store.items.map((c) => (
            <button
              key={c.entity?.id}
              onClick={async () => {
                // Load the full campaign — the list payload may be trimmed.
                let full = c;
                if (c.entity?.id) {
                  try { full = await store.GetCampaign(c.entity.id); } catch { full = c; }
                }
                setSelected(full);
              }}
              className={`w-full rounded-md border p-3 text-left hover:bg-muted ${selected?.entity?.id === c.entity?.id ? 'border-primary' : 'border-border'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground">{c.name}</span>
                <CampaignStatusBadge status={c.status} />
              </div>
              <div className="mt-1 flex flex-wrap gap-3 text-sm text-muted-foreground">
                <span>{t('affiliate.dashboard.clicks')}: {c.total_clicks}</span>
                <span>{t('affiliate.dashboard.conversions')}: {c.total_conversions}</span>
                <span>{formatMoney(c.total_earned, locale)}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <CampaignDetailRail
          campaign={selected}
          onClose={() => setSelected(null)}
          onEdit={() => { setEditing(selected); setEditOpen(true); }}
          onChanged={load}
        />
      )}

      <CampaignFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        campaign={editing}
        onSaved={(saved) => { setSelected(saved); load(); }}
      />
    </div>
  );
});
